import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Check, Loader2, Crown, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { API } from '@/utils/api';

export default function PaymentHandler({ onSuccess }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [checking, setChecking] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (paymentStatus === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Płatność anulowana');
      // Clear params
      searchParams.delete('payment');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Nie udało się zweryfikować płatności. Sprawdź email.');
      searchParams.delete('payment');
      searchParams.delete('session_id');
      setSearchParams(searchParams);
      return;
    }

    setChecking(true);

    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        toast.success(`Plan ${res.data.plan?.toUpperCase()} aktywowany! 🎉`);
        await refreshUser();
        
        // Clear params
        searchParams.delete('payment');
        searchParams.delete('session_id');
        setSearchParams(searchParams);
        
        if (onSuccess) onSuccess(res.data.plan);
        setChecking(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  if (checking) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      >
        <div className="bg-[#10182B] rounded-3xl p-8 text-center border border-white/10 max-w-md">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Weryfikacja płatności...</h3>
          <p className="text-zinc-400">Proszę czekać, sprawdzamy status Twojej płatności.</p>
        </div>
      </motion.div>
    );
  }

  return null;
}

// Upgrade button component
export function UpgradeButton({ plan = 'pro', variant = 'default', className = '', children }) {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleUpgrade = async () => {
    // Jeśli user nie jest zalogowany, przekieruj do logowania
    if (!user) {
      toast.info('Zaloguj się, aby aktywować plan');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payments/checkout`, {
        plan,
        origin_url: originUrl
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Błąd tworzenia sesji płatności');
      setLoading(false);
    }
  };

  const defaultContent = plan === 'premium' ? (
    <>
      <Crown className="w-4 h-4 mr-2" />
      Aktywuj PREMIUM
    </>
  ) : (
    <>
      <Zap className="w-4 h-4 mr-2" />
      Aktywuj PRO
    </>
  );

  return (
    <Button
      onClick={handleUpgrade}
      disabled={loading}
      className={`${
        plan === 'premium' 
          ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30' 
          : 'bg-blue-500 hover:bg-blue-600'
      } rounded-full ${className}`}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Przekierowanie...
        </>
      ) : (
        children || defaultContent
      )}
    </Button>
  );
}

// Plan badge component
export function PlanBadge({ plan, showUpgrade = true }) {
  const planConfig = {
    free: { 
      label: 'FREE', 
      className: 'bg-zinc-500/20 text-zinc-400 border-gray-500/30',
      icon: null 
    },
    pro: { 
      label: 'PRO', 
      className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      icon: Zap 
    },
    premium: { 
      label: 'PREMIUM', 
      className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      icon: Crown 
    },
  };

  const config = planConfig[plan] || planConfig.free;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-2">
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
        {Icon && <Icon className="w-3 h-3 inline mr-1" />}
        {config.label}
      </span>
      {showUpgrade && plan === 'free' && (
        <a href="/oferta" className="text-xs text-blue-400 hover:underline flex items-center gap-1">
          Ulepsz <ArrowRight className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}
